import React from "react";

/**
 * Inline-SVG-Diagramme für die ET-Hilfe.
 *
 * Iter 19.63.108 — Bewusst klein, monochrom, brand-aligned. Keine
 * externen Asset-Files, alles inline für Print-Freundlichkeit und
 * sofortiges Hot-Reload bei Inhaltsänderungen.
 *
 * Farbpalette: brand-400 (#E5A653 Default-Accent) + ink-Töne.
 */

/** 30/360-Konvention visualisiert als 12 gleichbreite Monats-Blöcke. */
export function Diagram30360() {
  const months = [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ];
  return (
    <figure className="my-6" data-testid="help-diagram-30360">
      <svg
        viewBox="0 0 720 140"
        className="w-full max-w-3xl border border-ink-200 rounded-sm bg-white"
        role="img"
        aria-label="30/360-Konvention: 12 gleichbreite Monatsblöcke à 30 Tage"
      >
        {/* Title */}
        <text
          x="20"
          y="22"
          fontSize="11"
          fontWeight="600"
          fill="#52443c"
          textAnchor="start"
        >
          ET-Rechenmethode: jedes Monat = 30 Tage, 1 Jahr = 360 Tage
        </text>
        {/* Month boxes */}
        {months.map((m, i) => (
          <g key={m}>
            <rect
              x={20 + i * 56}
              y={50}
              width={52}
              height={50}
              fill={i % 2 === 0 ? "#fcf4ea" : "#f7e5cb"}
              stroke="#d9b27a"
              strokeWidth="1"
            />
            <text
              x={20 + i * 56 + 26}
              y={72}
              fontSize="10"
              fontWeight="600"
              fill="#52443c"
              textAnchor="middle"
            >
              {m}
            </text>
            <text
              x={20 + i * 56 + 26}
              y={88}
              fontSize="9"
              fill="#52443c"
              textAnchor="middle"
            >
              30 T
            </text>
          </g>
        ))}
        {/* Sum line */}
        <text
          x="360"
          y="125"
          fontSize="10"
          fontWeight="600"
          fill="#52443c"
          textAnchor="middle"
        >
          12 × 30 T = 360 T = 1 Jahr
        </text>
      </svg>
      <figcaption className="mt-2 text-xs text-warm-600 max-w-3xl">
        ET rechnet alle Beschäftigungsdauern in dieser kaufmännischen
        Konvention. Branchenüblich im KV-Bereich, weil sie Mathematik und
        Vergleichbarkeit erleichtert. Das echte Kalenderjahr hat 365 Tage —
        in ET zählt es 360.
      </figcaption>
    </figure>
  );
}

/** Cap-Bypass-Kette als horizontale Timeline. */
export function DiagramCapBypassChain() {
  return (
    <figure className="my-6" data-testid="help-diagram-cap-bypass">
      <svg
        viewBox="0 0 720 200"
        className="w-full max-w-3xl border border-ink-200 rounded-sm bg-white"
        role="img"
        aria-label="Sonderregel Selber Arbeitgeber: Lücken zwischen Positionen desselben Arbeitgebers"
      >
        {/* Title */}
        <text
          x="20"
          y="22"
          fontSize="11"
          fontWeight="600"
          fill="#52443c"
        >
          Sonderregel „Selber Arbeitgeber“ — zusammenhängende Beschäftigung
          darf max. 24 Monate unterbrochen sein
        </text>

        {/* Timeline */}
        <line
          x1="20"
          y1="110"
          x2="700"
          y2="110"
          stroke="#d6cfc8"
          strokeWidth="1"
        />

        {/* Position 1 (OK) */}
        <rect x="60" y="90" width="120" height="40" fill="#dbeac5" stroke="#7a9e3a" />
        <text x="120" y="115" fontSize="11" fontWeight="600" fill="#3d5012" textAnchor="middle">
          Pos. 1
        </text>
        {/* Gap 1 (≤ 24 M) */}
        <text x="210" y="80" fontSize="10" fill="#52443c" textAnchor="middle">
          12 M.
        </text>
        <line x1="180" y1="110" x2="240" y2="110" stroke="#7a9e3a" strokeWidth="2" strokeDasharray="2 2" />
        {/* Position 2 (OK) */}
        <rect x="240" y="90" width="120" height="40" fill="#dbeac5" stroke="#7a9e3a" />
        <text x="300" y="115" fontSize="11" fontWeight="600" fill="#3d5012" textAnchor="middle">
          Pos. 2
        </text>
        {/* Gap 2 (> 24 M — Bruch!) */}
        <text x="390" y="80" fontSize="10" fontWeight="600" fill="#9b3d12" textAnchor="middle">
          36 M.
        </text>
        <line x1="360" y1="110" x2="420" y2="110" stroke="#c2410c" strokeWidth="2" strokeDasharray="2 2" />
        <text x="390" y="155" fontSize="9" fontWeight="600" fill="#9b3d12" textAnchor="middle">
          Beschäftigung unterbrochen
        </text>
        {/* Position 3 (kompromittiert) */}
        <rect x="420" y="90" width="120" height="40" fill="#fde7d3" stroke="#c2410c" />
        <text x="480" y="115" fontSize="11" fontWeight="600" fill="#9b3d12" textAnchor="middle">
          Pos. 3
        </text>
        {/* Gap zum Stichtag */}
        <text x="610" y="80" fontSize="10" fill="#52443c" textAnchor="middle">
          1 M.
        </text>
        <line x1="540" y1="110" x2="680" y2="110" stroke="#c2410c" strokeWidth="2" strokeDasharray="2 2" />
        {/* Stichtag */}
        <line x1="680" y1="80" x2="680" y2="140" stroke="#52443c" strokeWidth="2" />
        <text x="680" y="155" fontSize="10" fontWeight="600" fill="#52443c" textAnchor="middle">
          Stichtag
        </text>

        {/* Legend */}
        <rect x="20" y="170" width="14" height="10" fill="#dbeac5" stroke="#7a9e3a" />
        <text x="40" y="180" fontSize="9" fill="#52443c">
          zusammenhängend
        </text>
        <rect x="140" y="170" width="14" height="10" fill="#fde7d3" stroke="#c2410c" />
        <text x="160" y="180" fontSize="9" fill="#52443c">
          unterbrochen (Lücke &gt; 24 M.)
        </text>
      </svg>
      <figcaption className="mt-2 text-xs text-warm-600 max-w-3xl">
        Sobald an EINER Stelle in der Arbeitgeber-Abfolge die Lücke 24 Monate
        überschreitet, gilt die GESAMTE Abfolge als unterbrochen — auch
        Positionen davor verlieren die Sonderregel-Anrechnung.
      </figcaption>
    </figure>
  );
}

/** Workflow-Diagramm: 4 Schritte horizontal. */
export function DiagramWorkflow() {
  const steps = [
    { num: "1", title: "KV wählen", subtitle: "+ Stichtag" },
    { num: "2", title: "Dokumente", subtitle: "hochladen" },
    { num: "3", title: "Vordienst", subtitle: "prüfen" },
    { num: "4", title: "Empfehlung", subtitle: "+ PDF-Export" },
  ];
  return (
    <figure className="my-6" data-testid="help-diagram-workflow">
      <svg
        viewBox="0 0 720 140"
        className="w-full max-w-3xl border border-ink-200 rounded-sm bg-white"
        role="img"
        aria-label="ET-Workflow in 4 Schritten"
      >
        <text x="20" y="22" fontSize="11" fontWeight="600" fill="#52443c">
          Workflow: vom KV bis zur Empfehlung in 4 Schritten
        </text>
        {steps.map((s, i) => {
          const cx = 80 + i * 170;
          return (
            <g key={s.num}>
              <circle cx={cx} cy={75} r={28} fill="#fcf4ea" stroke="#d9b27a" strokeWidth="2" />
              <text x={cx} y={82} fontSize="22" fontWeight="700" fill="#52443c" textAnchor="middle">
                {s.num}
              </text>
              <text x={cx} y={120} fontSize="11" fontWeight="600" fill="#1f1813" textAnchor="middle">
                {s.title}
              </text>
              <text x={cx} y={132} fontSize="9" fill="#52443c" textAnchor="middle">
                {s.subtitle}
              </text>
              {i < steps.length - 1 && (
                <line
                  x1={cx + 32}
                  y1={75}
                  x2={cx + 138}
                  y2={75}
                  stroke="#d9b27a"
                  strokeWidth="1.5"
                  markerEnd="url(#wf-arrow)"
                />
              )}
            </g>
          );
        })}
        <defs>
          <marker id="wf-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto">
            <path d="M0,0 L10,5 L0,10 Z" fill="#d9b27a" />
          </marker>
        </defs>
      </svg>
      <figcaption className="mt-2 text-xs text-warm-600 max-w-3xl">
        Jeder Schritt baut auf dem vorherigen auf. Du kannst jederzeit
        zurückspringen und Angaben korrigieren — ET rechnet sofort neu.
      </figcaption>
    </figure>
  );
}

/** Cap-Aufschlüsselung: Roh / Cap-reduziert / Bypass — gestapelter Balken. */
export function DiagramCapBreakdown() {
  return (
    <figure className="my-6" data-testid="help-diagram-cap-breakdown">
      <svg
        viewBox="0 0 720 200"
        className="w-full max-w-3xl border border-ink-200 rounded-sm bg-white"
        role="img"
        aria-label="Aufschlüsselung der Höchstgrenze: Rohdauer vs. anrechenbar"
      >
        <text x="20" y="22" fontSize="11" fontWeight="600" fill="#52443c">
          Beispiel: 19 J. 2 M. rohe Vordienstzeit — was ist anrechenbar?
        </text>

        {/* Roh-Balken */}
        <text x="20" y="60" fontSize="10" fontWeight="600" fill="#52443c">
          Rohe Summe
        </text>
        <rect x="20" y="68" width="680" height="22" fill="#f0e3d1" stroke="#d6cfc8" />
        <text x="360" y="84" fontSize="10" fontWeight="600" fill="#1f1813" textAnchor="middle">
          19 J. 2 M.
        </text>

        {/* Höchstgrenze + Sonderregel-Balken */}
        <text x="20" y="115" fontSize="10" fontWeight="600" fill="#52443c">
          Anrechenbar (= Höchstgrenze + Sonderregel)
        </text>
        {/* Regulär bis Höchstgrenze = 7 J. */}
        <rect x="20" y="123" width="250" height="22" fill="#dbeac5" stroke="#7a9e3a" />
        <text x="145" y="139" fontSize="10" fontWeight="600" fill="#3d5012" textAnchor="middle">
          7 J. (Höchstgrenze)
        </text>
        {/* Sonderregel = 7 J. 1 M. */}
        <rect x="270" y="123" width="253" height="22" fill="#fcf4ea" stroke="#d9b27a" />
        <text x="397" y="139" fontSize="10" fontWeight="600" fill="#52443c" textAnchor="middle">
          + 7 J. 1 M. (Selber Arbeitgeber)
        </text>
        {/* Verworfen = 5 J. 1 M. */}
        <rect x="523" y="123" width="177" height="22" fill="#fde7d3" stroke="#c2410c" strokeDasharray="3 2" />
        <text x="611" y="139" fontSize="10" fontWeight="600" fill="#9b3d12" textAnchor="middle">
          5 J. 1 M. verworfen
        </text>

        {/* Endergebnis */}
        <text x="20" y="175" fontSize="11" fontWeight="700" fill="#1f1813">
          → ET-Empfehlung basiert auf: 14 J. 1 M. (= 7 J. Höchstgrenze + 7 J. 1 M. Sonderregel)
        </text>
      </svg>
      <figcaption className="mt-2 text-xs text-warm-600 max-w-3xl">
        Die Höchstanrechnungsdauer begrenzt reguläre Vordienstzeiten.
        Die Sonderregel „Selber Arbeitgeber“ rechnet zusätzlich darüber
        hinaus an, sofern die zusammenhängende Beschäftigung beim selben
        Arbeitgeber nicht unterbrochen ist. Was ÜBER beide Limits hinaus­
        geht, fließt nicht in die Einstufung ein.
      </figcaption>
    </figure>
  );
}
